/**
 * 管理员管理的数据请求（TanStack Query），接口与参数与旧版 AdminManagementView.vue 一致
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/api/services/admin'
import type { AdminDetailInfo, AdminListItem } from '@/types/admin'

const ADMINS_KEY = ['admin', 'admins'] as const

export interface AdminListFilters {
  role: string
  is_active?: boolean
  search: string
}

export interface AdminFormValues {
  username: string
  email: string
  password?: string
  display_name?: string
  role?: string
}

// 接口错误信息：后端在 message 字段里给出原因
export function apiErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.message || fallback
}

export function useAdminList(page: number, pageSize: number, filters: AdminListFilters) {
  return useQuery({
    queryKey: [...ADMINS_KEY, 'list', page, pageSize, filters],
    queryFn: async () => {
      const response = await adminApi.getAdminList({
        page,
        page_size: pageSize,
        search: filters.search || undefined,
        role: filters.role || undefined,
        is_active: filters.is_active
      })
      const result = response.data.data ?? {}
      return { admins: (result.data ?? []) as AdminListItem[], total: (result.total ?? 0) as number }
    },
    placeholderData: keepPreviousData
  })
}

export function useAdminDetail(id: number | null) {
  return useQuery({
    queryKey: [...ADMINS_KEY, 'detail', id],
    enabled: id !== null,
    queryFn: async () => (await adminApi.getAdminDetail(id as number)).data.data as AdminDetailInfo
  })
}

export function useSaveAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, values }: { id?: number; values: AdminFormValues }) =>
      id === undefined
        ? adminApi.createAdmin({
            username: values.username,
            email: values.email,
            password: values.password ?? '',
            display_name: values.display_name,
            role: values.role ?? ''
          })
        : adminApi.updateAdmin(id, { email: values.email, display_name: values.display_name }),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMINS_KEY })
  })
}

export function useToggleAdminStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (admin: AdminListItem) => adminApi.updateAdminStatus(admin.id, !admin.is_active),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMINS_KEY })
  })
}

export function useDeleteAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => adminApi.deleteAdmin(id),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMINS_KEY })
  })
}
